import { FC, useEffect, useState } from "react";
import { createUseStyles } from "react-jss";
import { useTranslation } from "react-i18next";
import { CustomModal } from "../../../../components/CustomModal/CustomModal";
import { CustomTextInput } from "../../../../components/CustomTextInput/CustomTextInput";
import { useUpdatePassword } from "../../../../api/user";
import {
  describePolicy,
  usePasswordPolicy,
  validatePassword,
} from "../../../../api/passwordPolicy";

const useStyles = createUseStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "12px",
    minWidth: "min(380px, 78vw)",
    width: "100%",
    boxSizing: "border-box",
    padding: "8px 0px",
  },
  field: { display: "flex", flexDirection: "column" },
  hint: {
    color: "#6B7280",
    fontFamily: "Poppins-Regular",
    fontSize: "11px",
    paddingLeft: "26px",
    marginTop: "2px",
  },
  error: {
    color: "#EF4444",
    fontFamily: "Poppins-Regular",
    fontSize: "11px",
    paddingLeft: "26px",
    marginTop: "2px",
  },
});

// La regle vit dans api/passwordPolicy.ts, alimentee par le serveur
// (config/security.php). Elle etait recopiee ici sous forme de constante :
// quatre ecrans portaient quatre versions de la meme regle, dont deux
// incompletes.

type Props = {
  open: boolean;
  user: TUser | null;
  onClose: () => void;
};

/**
 * Self-service password reset for the signed-in user.
 *
 * Wired to PUT /user/update { id, password } (the same endpoint the
 * forgot-password flow ends with). We deliberately do NOT clear the session
 * here — the token is unaffected by the change, so the user stays signed in.
 */
export const ModalChangePassword: FC<Props> = ({ open, user, onClose }) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState<string | null>(null);

  const { mutate: updatePassword, isPending } = useUpdatePassword(() => onClose());
  const passwordPolicy = usePasswordPolicy();

  useEffect(() => {
    if (open) {
      setPassword("");
      setConfirm("");
      setError(null);
    }
  }, [open]);

  const canSubmit = password.trim().length > 0 && confirm.trim().length > 0;

  const onSave = () => {
    const pwd = password.trim();
    if (!pwd || !confirm.trim()) return;
    if (pwd !== confirm.trim()) {
      setError(t("login.errorConfirm", "Les mots de passe ne correspondent pas"));
      return;
    }
    if (!validatePassword(pwd, passwordPolicy)) {
      setError(describePolicy(t, passwordPolicy));
      return;
    }
    setError(null);
    if (!user?.id) return;
    updatePassword({ id: user.id, password: pwd });
  };

  return (
    <CustomModal
      open={open}
      title={t("setting.resetPassword", "Reinitialiser le mot de passe")}
      onClose={onClose}
      onCancel={onClose}
      onSave={onSave}
      loadingSave={isPending}
      disabledSaveButton={!canSubmit}
    >
      <div className={classes.container}>
        <div className={classes.field}>
          <CustomTextInput
            label={t("setting.newPasswordLabel", "Nouveau mot de passe")}
            placeholder={t("setting.newPasswordLabel", "Nouveau mot de passe")}
            marginBottom={"0px"}
            name="password"
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />
          <span className={classes.hint}>
            {t(
              "setting.passwordHint",
              "8 caracteres min., une majuscule, une minuscule, un chiffre et un caractere special."
            )}
          </span>
        </div>
        <div className={classes.field}>
          <CustomTextInput
            label={t("setting.confirmPasswordLabel", "Confirmer le nouveau mot de passe")}
            placeholder={t("setting.confirmPasswordLabel", "Confirmer le nouveau mot de passe")}
            marginBottom={"0px"}
            name="confirm"
            type="password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
          />
        </div>
        {error && <span className={classes.error}>{error}</span>}
      </div>
    </CustomModal>
  );
};